/**
 * TopBar bell — polls /v1/alerts/unread-count every 15s and exposes a
 * dropdown listing the most recent unread alerts. Clicking an alert
 * navigates to the lead and marks it read; "Mark all as read" clears
 * the whole queue.
 *
 * Per-lead red dots elsewhere in the app are driven by
 * /v1/alerts/unread-lead-ids; pages that need them (Leads list, Lead
 * Detail) call alertsService.unreadLeadIds() and `useUnreadLeads()` —
 * see lib/unreadLeads.jsx.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertsService } from '../lib/services';

const POLL_MS = 15000;
const PAGE_SIZE = 30;    // unread rows fetched per "Load more"
const MAX_FETCH = 200;   // backend caps listUnread() at 200 — see LeadAlertService

const TYPE_LABEL = {
  HANDOFF_REQUESTED:   'Handoff requested',
  PAUSED_LEAD_INBOUND: 'New message',
  AI_AUTO_RESUMED:     'AI auto-resumed',
  TAKEOVER_STARTED:    'Consultant takeover',
  HAND_BACK_TO_AI:     'AI resumed',
  APPOINTMENT_BOOKED:  'Appointment booked',
};

const TYPE_ICON = {
  HANDOFF_REQUESTED:   '🚨',
  PAUSED_LEAD_INBOUND: '💬',
  AI_AUTO_RESUMED:     '↺',
  TAKEOVER_STARTED:    '✋',
  HAND_BACK_TO_AI:     '🤖',
  APPOINTMENT_BOOKED:  '📅',
};

function fmtAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (secs < 60)      return `${secs}s ago`;
  if (secs < 3600)    return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400)   return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // How many unread rows the panel currently shows. Grows via "Load more" so
  // the whole unread set is reachable, not just the first page (DEF: badge 131
  // but only ~30 scrollable).
  const [limit, setLimit] = useState(PAGE_SIZE);
  const ref = useRef(null);
   // Tracks consecutive poll failures so we can back off instead of
  // hammering a dead/unauthenticated endpoint every 15s forever.
  const failStreak = useRef(0);

  const refreshCount = useCallback(async () => {
    try { setCount(await alertsService.unreadCount());
      failStreak.current = 0;
     }
    catch (err) { 
      failStreak.current = 0;
      if (err?.response?.status === 401 && failStreak.current === 3) {
        console.warn('[NotificationBell] unread-count auth failing repeatedly - pausing polls until next reload.');
      }
      /* keep previous */ 
    }
  }, []);

  const refreshList = useCallback(async (lim) => {
    setLoading(true);
    try {
      const rows = await alertsService.list({ unread: true, limit: lim });
      setItems(Array.isArray(rows) ? rows : []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = () => {
    const next = limit + PAGE_SIZE;
    setLimit(next);
    refreshList(next);
  };

  // Background poll the count.
  useEffect(() => {
    refreshCount();
    const t = setInterval(() => {
      if (failStreak.current >= 3) return;
      refreshCount();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [refreshCount]);

  // When the dropdown opens, reset to the first page and pull the list.
  useEffect(() => {
    if (open) { setLimit(PAGE_SIZE); refreshList(PAGE_SIZE); }
  }, [open, refreshList]);

  // Click-outside closes the menu.
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const onPickItem = async (alert) => {
    setOpen(false);
    try { await alertsService.read(alert.id); } catch (_) {}
    setItems(prev => prev.filter(a => a.id !== alert.id));
    setCount(c => Math.max(0, c - 1));
    if (alert.leadId) navigate(`/leads/${alert.leadId}`);
  };

  const onMarkAllRead = async () => {
    try {
      await alertsService.readAll();
      setItems([]);
      setCount(0);
    } catch (_) {}
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="topbar-theme-btn"
        onClick={() => setOpen(o => !o)}
        title="Notifications"
        aria-label="Notifications"
        style={{ position: 'relative' }}
      >
        🔔
        {count > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 10,
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '16px',
              background: 'var(--danger)',
              color: '#fff',
              textAlign: 'center',
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <div role="menu" className="topbar-menu" style={{ minWidth: 340, maxWidth: 380 }}>
          <div className="topbar-menu-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Notifications</div>
              <div className="mono text-xs text-muted">
                {count === 0 ? 'You’re all caught up' : `${count} unread`}
              </div>
            </div>
            {count > 0 && (
              <button className="btn small" onClick={onMarkAllRead} title="Mark all as read">
                Mark all read
              </button>
            )}
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {loading && (
              <div className="topbar-menu-item" style={{ cursor: 'default', opacity: 0.7 }}>
                Loading…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="topbar-menu-item" style={{ cursor: 'default', opacity: 0.7 }}>
                <span aria-hidden="true">✓</span> Nothing to review right now.
              </div>
            )}
            {!loading && items.map(a => (
              <button
                key={a.id}
                className="topbar-menu-item"
                onClick={() => onPickItem(a)}
                style={{ alignItems: 'flex-start', gap: 10, padding: '8px 12px' }}
              >
                <span aria-hidden="true" style={{ fontSize: 16 }}>{TYPE_ICON[a.type] || '•'}</span>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>
                    {a.title || TYPE_LABEL[a.type] || a.type}
                  </div>
                  {a.body && (
                    <div className="text-xs text-muted" style={{ marginTop: 2, lineHeight: 1.4 }}>
                      {a.body.length > 120 ? a.body.slice(0, 120) + '…' : a.body}
                    </div>
                  )}
                  <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
                    {a.severity !== 'INFO' && (
                      <span style={{
                        marginRight: 8,
                        padding: '0 6px',
                        borderRadius: 4,
                        background: a.severity === 'URGENT'
                          ? 'rgba(239,68,68,0.18)' : 'rgba(245,158,11,0.18)',
                        color: a.severity === 'URGENT'
                          ? 'var(--danger)' : 'var(--warning)',
                      }}>{a.severity}</span>
                    )}
                    {fmtAgo(a.createdAt)}
                  </div>
                </div>
              </button>
            ))}

            {!loading && items.length > 0 && items.length < count && (
              <div style={{ padding: '8px 12px', textAlign: 'center' }}>
                <div className="mono text-xs text-muted" style={{ marginBottom: 6 }}>
                  Showing {items.length} of {count}
                </div>
                {items.length < MAX_FETCH ? (
                  <button type="button" className="btn small" style={{ width: '100%' }}
                          onClick={loadMore}>
                    Load more ({count - items.length} more)
                  </button>
                ) : (
                  <div className="text-xs text-muted">
                    Showing the {MAX_FETCH} most recent — mark some as read to see older ones.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
